import { z } from "zod";
import { QaScope, QaCitationSourceType } from "./qa.js";
/**
 * Knowledge-hub API contract. A KnowledgeTopic is the human-curated seed
 * (title + instruction + optional scope); the generation pipeline synthesizes
 * grounded KnowledgePage revisions from retrieved code + Jira evidence, which
 * are reviewed before publish.
 */
/** Lifecycle of a generated page revision. */
export const KnowledgePageStatus = z.enum([
    /** Generation enqueued/running; body not yet final. */
    "generating",
    /** Generated, awaiting human review. */
    "draft",
    /** Approved and live on the hub. */
    "published",
    /** A previously-published page whose sources have since changed; a fresh
     *  draft is being / has been generated for review. */
    "stale",
    /** Reviewer declined this revision. */
    "rejected",
]);
/** Topic status — active topics participate in scheduled refresh. */
export const KnowledgeTopicStatus = z.enum(["active", "archived"]);
/** Citation entry attached to a generated page (mirrors QA citations). */
export const KnowledgeCitation = z.object({
    sourceType: QaCitationSourceType,
    sourceId: z.string(),
    label: z.string(),
});
/** One verifier verdict for a section/claim of a generated page. */
export const KnowledgeCoverageEntry = z.object({
    /** Heading or short identifier for the section this verdict covers. */
    section: z.string(),
    /** Whether the section's claims trace to a cited source in the evidence set. */
    supported: z.boolean(),
    /** Source ids the section relies on. */
    citedIds: z.array(z.string()),
    /** Verifier note — e.g. why a section is unsupported. */
    note: z.string().optional(),
});
export const KnowledgeCoverage = z.object({
    entries: z.array(KnowledgeCoverageEntry),
    /** True when every section is supported (publish is otherwise blocked). */
    allSupported: z.boolean(),
});
// --- Topics ---------------------------------------------------------------
export const CreateKnowledgeTopicRequest = z.object({
    title: z.string().min(1),
    /** Instruction describing what the entry should cover. */
    prompt: z.string().min(1),
    scope: QaScope.optional(),
});
export const UpdateKnowledgeTopicRequest = z.object({
    title: z.string().min(1).optional(),
    prompt: z.string().min(1).optional(),
    scope: QaScope.nullable().optional(),
    status: KnowledgeTopicStatus.optional(),
});
export const KnowledgeTopicDto = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    title: z.string(),
    prompt: z.string(),
    scope: QaScope.nullable(),
    status: KnowledgeTopicStatus,
    publishedPageId: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const KnowledgeTopicsListResponse = z.object({
    topics: z.array(KnowledgeTopicDto),
});
// --- Pages ----------------------------------------------------------------
export const KnowledgePageDto = z.object({
    id: z.string().uuid(),
    topicId: z.string().uuid(),
    version: z.number().int().positive(),
    status: KnowledgePageStatus,
    title: z.string(),
    body: z.string(),
    citations: z.array(KnowledgeCitation),
    coverage: KnowledgeCoverage.nullable(),
    sourceFingerprint: z.string().nullable(),
    generatedAt: z.string().datetime(),
    publishedAt: z.string().datetime().nullable(),
});
export const KnowledgePagesListResponse = z.object({
    pages: z.array(KnowledgePageDto),
});
/** Page detail, including the currently-published body so the UI can diff. */
export const KnowledgePageDetailResponse = z.object({
    page: KnowledgePageDto,
    /** The live page for the same topic, when one exists and differs. Lets the
     *  review UI show a diff of the draft against what's currently published. */
    publishedPage: KnowledgePageDto.nullable(),
});
// --- Actions --------------------------------------------------------------
export const KnowledgeIdParam = z.object({ id: z.string().uuid() });
/** Response for enqueue-style actions (generate). */
export const KnowledgeGenerateResponse = z.object({
    topicId: z.string().uuid(),
    enqueued: z.boolean(),
});
// --- Public hub -----------------------------------------------------------
/** A published entry as shown on the human-facing hub. */
export const KnowledgeHubEntry = z.object({
    topicId: z.string().uuid(),
    slug: z.string(),
    title: z.string(),
    body: z.string(),
    citations: z.array(KnowledgeCitation),
    publishedAt: z.string().datetime().nullable(),
    /** True when the live page's sources have since changed (update pending). */
    stale: z.boolean(),
});
export const KnowledgeHubResponse = z.object({
    entries: z.array(KnowledgeHubEntry),
});
//# sourceMappingURL=knowledge.js.map